import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { ArrowLeft, Phone, MessageCircle } from "lucide-react";
import { fetchHelper } from "@/lib/api";

export default function Biodata() {
  const { id } = useParams();
  const [helper, setHelper] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  useEffect(() => {
    setLoading(true);
    fetchHelper(id)
      .then((d) => { setHelper(d); setLoading(false); })
      .catch(() => { setError(true); setLoading(false); });
  }, [id]);

  if (loading) return <div className="max-w-7xl mx-auto px-6 py-32 text-center text-gray-500" data-testid="biodata-loading">Loading…</div>;
  if (error || !helper) return (
    <div className="max-w-7xl mx-auto px-6 py-32 text-center">
      <div className="font-serif text-3xl text-[#0A1128] mb-4">Helper not found</div>
      <Link to="/search-maid" className="btn-outline">Back to Search</Link>
    </div>
  );

  return (
    <div data-testid="biodata-page">
      <div className="max-w-7xl mx-auto px-6 sm:px-10 pt-10">
        <Link to="/search-maid" data-testid="biodata-back" className="text-sm text-gray-600 hover:text-[#688A4E] inline-flex items-center gap-2">
          <ArrowLeft className="w-4 h-4"/> Back to Search
        </Link>
      </div>

      <section className="max-w-7xl mx-auto px-6 sm:px-10 py-12 grid lg:grid-cols-12 gap-12">
        <div className="lg:col-span-5">
          <div className="relative">
            <img src={helper.photo} alt={helper.name} className="w-full h-[560px] object-cover"/>
            <div className="absolute top-6 right-6 bg-[#688A4E] text-white px-4 py-3">
              <div className="text-[10px] tracking-widest uppercase opacity-80">Matching Score</div>
              <div className="font-serif text-3xl leading-none">{helper.matching_score}<span className="text-lg opacity-70">/100</span></div>
            </div>
          </div>
        </div>

        <div className="lg:col-span-7">
          <div className="overline mb-3">Biodata</div>
          <h1 className="font-serif text-5xl sm:text-6xl text-[#0A1128] leading-none mb-4">{helper.name}</h1>
          <div className="text-gray-600 mb-8 flex flex-wrap gap-x-4 gap-y-1">
            <span>{helper.age} years old</span>
            <span>·</span>
            <span>{helper.nationality}</span>
            <span>·</span>
            <span>{helper.experience_years} yrs experience</span>
          </div>

          <p className="text-lg text-gray-700 leading-relaxed mb-8">{helper.bio}</p>

          <div className="border-t border-gray-200 pt-6 mb-6">
            <div className="overline mb-3">Highlights</div>
            <ul className="space-y-2">
              {helper.highlights.map((h) => (
                <li key={h} className="flex gap-3"><span className="text-[#688A4E]">✦</span><span>{h}</span></li>
              ))}
            </ul>
          </div>

          <div className="grid grid-cols-2 gap-6 border-t border-gray-200 pt-6 text-sm">
            {[
              ["Education", helper.education],
              ["Marital Status", helper.marital_status],
              ["Children", String(helper.children)],
              ["Religion", helper.religion],
              ["Height", `${helper.height_cm} cm`],
              ["Weight", `${helper.weight_kg} kg`],
              ["Languages", helper.languages.join(", ")],
              ["Availability", helper.availability],
            ].map(([k, v]) => (
              <div key={k}>
                <div className="text-xs tracking-widest uppercase text-gray-400 mb-1">{k}</div>
                <div className="text-[#0A1128]">{v}</div>
              </div>
            ))}
          </div>

          <div className="mt-8 border-t border-gray-200 pt-6">
            <div className="overline mb-3">Skills</div>
            <div className="flex flex-wrap gap-2">
              {helper.skills.map((s) => (
                <span key={s} className="border border-[#0A1128] px-3 py-1.5 text-sm text-[#0A1128]">{s}</span>
              ))}
            </div>
          </div>

          <div className="mt-10 flex flex-wrap gap-4">
            <a href="https://wa.me/6591351039" target="_blank" rel="noreferrer" data-testid="biodata-whatsapp" className="btn-primary">
              <MessageCircle className="w-4 h-4"/> Enquire on WhatsApp
            </a>
            <a href="tel:+6591351039" data-testid="biodata-call" className="btn-outline">
              <Phone className="w-4 h-4"/> Call 9135 1039
            </a>
          </div>
        </div>
      </section>

      <section className="bg-[#0A1128] text-white">
        <div className="max-w-5xl mx-auto px-6 sm:px-10 py-16 text-center">
          <div className="overline text-[#688A4E] mb-4 justify-center">Not quite the one?</div>
          <h2 className="font-serif text-3xl sm:text-4xl mb-6">Let us shortlist candidates specifically for your family.</h2>
          <Link to="/application" className="btn-primary" data-testid="biodata-application-cta">Submit Application</Link>
        </div>
      </section>
    </div>
  );
}
